/**
 * Read-only display for the AP invoice payload last submitted to NEX (AP POST or return-slip PUT).
 */
import React from 'react';
import { Box, Flex, HStack, Table, Tbody, Td, Text, Thead, Th, Tr, VStack } from '@chakra-ui/react';
import type { InvoiceDetailResponse } from '../../types/invoice';

const NEX_TYPE_LABELS: Record<string, string> = {
    REG: 'Received',
    CM: 'Credit',
};

function invoiceTypeLabel(nexCode: string, invoice: InvoiceDetailResponse): string {
    if (nexCode === 'CM' && invoice.invoiceType === 'return_slip') return 'Return Slip';
    return NEX_TYPE_LABELS[nexCode] ?? nexCode;
}

function formatDate(iso: string): string {
    const [y, m, d] = iso.split('-');
    if (!y || !m || !d) return iso;
    return `${m}/${d}/${y}`;
}

function zeroDash(v: string | number): string {
    if (v === 0 || v === '0' || v === '$0.00' || v === '0.00') return '–';
    return String(v);
}

function fmtDollar(n: number, fmtUsd: (n: number) => string): string {
    if (n === 0) return '$0.00';
    if (n < 0) return `-$${fmtUsd(Math.abs(n))}`;
    return `$${fmtUsd(n)}`;
}

function fmtDollarUnit(n: number): string {
    const abs = Math.abs(Number.isFinite(n) ? n : 0);
    const formatted = abs.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 6 });
    return n < 0 ? `-$${formatted}` : `$${formatted}`;
}

const tableSx = {
    tableLayout: 'fixed' as const,
    borderCollapse: 'separate' as const,
    borderSpacing: 0,
    'thead tr:first-of-type th:first-of-type': { borderTopLeftRadius: '6px' },
    'thead tr:first-of-type th:last-of-type': { borderTopRightRadius: '6px' },
    'tbody tr:last-of-type td:first-of-type': { borderBottomLeftRadius: '6px' },
    'tbody tr:last-of-type td:last-of-type': { borderBottomRightRadius: '6px' },
};

function KvRow({ label, children, isLast }: { label: string; children: React.ReactNode; isLast?: boolean }) {
    return (
        <Flex align="center" w="100%" borderBottom={isLast ? 'none' : '1px solid'} borderColor="gray.100">
            <Text
                color="gray.500"
                fontSize="sm"
                w="180px"
                flexShrink={0}
                pl={5}
                py={3}
            >
                {label}
            </Text>
            <Box w="1px" alignSelf="stretch" bg="gray.100" />
            <HStack flex="1" spacing={2} pl={4} pr={5} py={3}>
                {children}
            </HStack>
        </Flex>
    );
}

function isManualLineInd(value: 0 | 1 | 2 | boolean | undefined): boolean {
    return value === true || (typeof value === 'number' && value !== 0);
}

function ManualTag({ value }: { value: 0 | 1 | 2 | boolean | undefined }) {
    const isManual = isManualLineInd(value);
    const label =
        typeof value === 'number' ? String(value) : value === true ? '1' : '0';
    return (
        <Box
            as="span"
            display="inline-block"
            px={2}
            py={0.5}
            borderRadius="md"
            fontSize="xs"
            fontWeight="semibold"
            bg={isManual ? 'green.50' : 'gray.100'}
            color={isManual ? 'green.700' : 'gray.600'}
            border="1px solid"
            borderColor={isManual ? 'green.200' : 'gray.200'}
        >
            {label}
        </Box>
    );
}

export interface InvoiceSubmittedNexPayloadSectionProps {
    invoice: InvoiceDetailResponse;
    fmtUsd: (n: number) => string;
    freightAsLineItem?: boolean;
    salesTaxAsLineItem?: boolean;
}

export function InvoiceSubmittedNexPayloadSection(props: InvoiceSubmittedNexPayloadSectionProps) {
    const { invoice, fmtUsd, freightAsLineItem = true, salesTaxAsLineItem = false } = props;
    const nex = invoice.nexSubmission;

    const isSubmittedSuccess =
        (invoice.status === 'submitted' || invoice.status === 'manually_submitted') && nex?.lastStatus === 'succeeded';
    const isFailedAttempt =
        invoice.status === 'flagged' && nex?.lastStatus === 'failed' && !!nex?.lastAttemptedApInvoicePayload;

    if (!isSubmittedSuccess && !isFailedAttempt) {
        return null;
    }

    const ap = isSubmittedSuccess ? nex?.lastSubmittedApInvoicePayload : nex?.lastAttemptedApInvoicePayload;
    const slips = nex?.lastSubmittedReturnSlipPayloads;

    if (!ap && (!slips || slips.length === 0)) {
        return (
            <Box>
                <Text color="gray.900" fontSize="sm" fontWeight="bold" mb={2}>
                    {isFailedAttempt ? 'Last attempted AP invoice' : 'Submitted AP Invoice'}
                </Text>
                <Text color="gray.600" fontSize="sm">
                    No stored submission payload for this invoice. Invoices submitted before this history was added will not show payload details here.
                </Text>
            </Box>
        );
    }

    const header = ap?.apHeader;
    const folderNo = ap?.apDist?.[0]?.folderNo;
    const sectionTitle = isFailedAttempt ? 'Last attempted AP invoice (NEX rejected)' : 'Submitted AP Invoice';
    const summaryLabel = isFailedAttempt ? 'Attempted AP summary' : 'Posted AP Summary';

    return (
        <VStack spacing={5} align="stretch">
            <Text color="gray.900" fontSize="sm" fontWeight="bold">
                {sectionTitle}
            </Text>

            {/* ── AP Header ── */}
            {header && (
                <Box>
                    <Text color="gray.700" fontSize="xs" fontWeight="semibold" mb={2} textTransform="uppercase" letterSpacing="0.04em">
                        {summaryLabel}
                    </Text>
                    <VStack
                        spacing={0}
                        bg="white"
                        borderRadius="lg"
                        border="1px solid"
                        borderColor="gray.200"
                        overflow="hidden"
                    >
                        <KvRow label="Folder Number">
                            <Text fontSize="sm" color="gray.900">{zeroDash(folderNo ?? 0)}</Text>
                        </KvRow>
                        <KvRow label="Invoice Number">
                            <Text fontSize="sm" color="gray.900">{header.invoiceNo || '—'}</Text>
                        </KvRow>
                        <KvRow label="Invoice Date">
                            <Text fontSize="sm" color="gray.900">{formatDate(header.invoiceDate)}</Text>
                        </KvRow>
                        <KvRow label="Invoice Amount">
                            <Text fontSize="sm" color="gray.900">{zeroDash(fmtDollar(header.invoiceAmount, fmtUsd))}</Text>
                        </KvRow>
                        {!freightAsLineItem && (
                            <KvRow label="Freight">
                                <Text fontSize="sm" color="gray.900">{zeroDash(fmtDollar(header.freight, fmtUsd))}</Text>
                            </KvRow>
                        )}
                        <KvRow label="Discount">
                            <Text fontSize="sm" color="gray.900">{zeroDash(fmtDollar(header.discount, fmtUsd))}</Text>
                        </KvRow>
                        {!salesTaxAsLineItem && (
                            (header as { gstHSTTax?: number }).gstHSTTax != null && ((header as { gstHSTTax?: number }).gstHSTTax! + ((header as { pstTax?: number }).pstTax ?? 0)) !== 0 && header.tax === 0 ? (
                                <>
                                    <KvRow label="GST/HST">
                                        <Text fontSize="sm" color="gray.900">{zeroDash(fmtDollar((header as { gstHSTTax?: number }).gstHSTTax!, fmtUsd))}</Text>
                                    </KvRow>
                                    <KvRow label="PST">
                                        <Text fontSize="sm" color="gray.900">{zeroDash(fmtDollar((header as { pstTax?: number }).pstTax ?? 0, fmtUsd))}</Text>
                                    </KvRow>
                                </>
                            ) : (
                                <KvRow label="Tax">
                                    <Text fontSize="sm" color="gray.900">{zeroDash(fmtDollar(header.tax, fmtUsd))}</Text>
                                </KvRow>
                            )
                        )}
                        <KvRow label="Total">
                            <Text fontSize="sm" color="gray.900">{zeroDash(fmtDollar(header.total, fmtUsd))}</Text>
                        </KvRow>
                        <KvRow label="Invoice Type" isLast>
                            <Text fontSize="sm" color="gray.900">{invoiceTypeLabel(header.invoiceType, invoice)}</Text>
                        </KvRow>
                    </VStack>
                </Box>
            )}

            {/* ── Distribution Lines ── */}
            {ap && ap.apDist && ap.apDist.length > 0 && (
                <Box>
                    <Text color="gray.700" fontSize="xs" fontWeight="semibold" mb={2} textTransform="uppercase" letterSpacing="0.04em">
                        Distribution Lines
                    </Text>
                    <Box border="1px solid" borderColor="gray.200" borderRadius="lg" overflow="hidden">
                        <Table variant="simple" size="sm" sx={tableSx}>
                            <Thead bg="gray.50">
                                <Tr>
                                    <Th py={3} w="8%" color="gray.500" borderColor="gray.200" fontSize="xs">
                                        PO Line
                                    </Th>
                                    <Th py={3} w="10%" color="gray.500" borderColor="gray.200" fontSize="xs">
                                        PO No.
                                    </Th>
                                    <Th py={3} w="22%" color="gray.500" borderColor="gray.200" fontSize="xs">
                                        Description
                                    </Th>
                                    <Th py={3} w="8%" color="gray.500" borderColor="gray.200" fontSize="xs" isNumeric>
                                        Qty
                                    </Th>
                                    <Th py={3} w="10%" color="gray.500" borderColor="gray.200" fontSize="xs" isNumeric>
                                        List
                                    </Th>
                                    <Th py={3} w="10%" color="gray.500" borderColor="gray.200" fontSize="xs" isNumeric>
                                        Cost
                                    </Th>
                                    <Th py={3} w="11%" color="gray.500" borderColor="gray.200" fontSize="xs" isNumeric>
                                        Total
                                    </Th>
                                    <Th py={3} w="15%" color="gray.500" borderColor="gray.200" fontSize="xs" textAlign="center">
                                        Manual Line
                                    </Th>
                                </Tr>
                            </Thead>
                            <Tbody>
                                {ap.apDist.map((row, idx) => {
                                    const lineTotal =
                                        typeof row.totalCost === 'number' ? row.totalCost : row.unitCost * row.units;
                                    return (
                                        <Tr key={`apdist-${idx}`}>
                                            <Td py={2} fontSize="sm" borderColor="gray.100">
                                                {zeroDash(row.poLineNo ?? 0)}
                                            </Td>
                                            <Td py={2} fontSize="sm" borderColor="gray.100">
                                                {zeroDash(row.poHeaderID ?? 0)}
                                            </Td>
                                            <Td py={2} fontSize="sm" color="gray.800" borderColor="gray.100" whiteSpace="normal" wordBreak="break-word">
                                                {row.description || '—'}
                                            </Td>
                                            <Td py={2} fontSize="sm" borderColor="gray.100" isNumeric>
                                                {zeroDash(row.units)}
                                            </Td>
                                            <Td py={2} fontSize="sm" borderColor="gray.100" isNumeric>
                                                {zeroDash(fmtDollarUnit(row.unitList))}
                                            </Td>
                                            <Td py={2} fontSize="sm" borderColor="gray.100" isNumeric>
                                                {zeroDash(fmtDollarUnit(row.unitCost))}
                                            </Td>
                                            <Td py={2} fontSize="sm" borderColor="gray.100" isNumeric>
                                                {zeroDash(fmtDollar(lineTotal, fmtUsd))}
                                            </Td>
                                            <Td py={2} borderColor="gray.100" textAlign="center">
                                                <ManualTag value={row.manualLineInd} />
                                            </Td>
                                        </Tr>
                                    );
                                })}
                            </Tbody>
                        </Table>
                    </Box>
                </Box>
            )}

            {/* ── Return Slips ── */}
            {/* {slips && slips.length > 0 && (
                <Box>
                    <Text color="gray.700" fontSize="xs" fontWeight="semibold" mb={2} textTransform="uppercase" letterSpacing="0.04em">
                        Return Slip{slips.length > 1 ? 's' : ''} (PUT)
                    </Text>
                    <Box border="1px solid" borderColor="gray.200" borderRadius="lg" overflow="hidden">
                        <Table variant="simple" size="sm" sx={tableSx}>
                            <Thead bg="gray.50">
                                <Tr>
                                    <Th py={3} w="10%" color="gray.500" borderColor="gray.200" fontSize="xs">
                                        #
                                    </Th>
                                    <Th py={3} w="12%" color="gray.500" borderColor="gray.200" fontSize="xs">
                                        Folder
                                    </Th>
                                    <Th py={3} w="14%" color="gray.500" borderColor="gray.200" fontSize="xs">
                                        PO Header
                                    </Th>
                                    <Th py={3} w="18%" color="gray.500" borderColor="gray.200" fontSize="xs">
                                        PO Lines
                                    </Th>
                                    <Th py={3} color="gray.500" borderColor="gray.200" fontSize="xs">
                                        Image URL
                                    </Th>
                                </Tr>
                            </Thead>
                            <Tbody>
                                {slips.map((slip, idx) => (
                                    <Tr key={`slip-${idx}`}>
                                        <Td py={2} fontSize="sm" borderColor="gray.100">
                                            {idx + 1}
                                        </Td>
                                        <Td py={2} fontSize="sm" borderColor="gray.100">
                                            {slip.folderNo}
                                        </Td>
                                        <Td py={2} fontSize="sm" borderColor="gray.100">
                                            {slip.poHeaderID}
                                        </Td>
                                        <Td py={2} fontSize="sm" fontFamily="mono" borderColor="gray.100">
                                            {slip.poLines?.length ? slip.poLines.join(', ') : '—'}
                                        </Td>
                                        <Td py={2} borderColor="gray.100">
                                            <UrlCell url={slip.imageURL} />
                                        </Td>
                                    </Tr>
                                ))}
                            </Tbody>
                        </Table>
                    </Box>
                </Box>
            )} */}
        </VStack>
    );
}
